// All site copy for Clarus Partners lives here - single source of truth.
// Items awaiting Nima's input are marked [NIMA TO PROVIDE] / [NIMA TO CONFIRM]
// and are rendered visibly as placeholders, never as facts.

export const SITE = {
  name: "Clarus Partners",
  email: "nima@claruspartners.com.au",
  domain: "https://claruspartners.com.au",
  calendlyUrl: "https://calendly.com/nima-claruspartners/30min",
  // NOTE: copy standardised to 30 minutes to match the live Calendly event
  // ("Complimentary Intro Call", 30 min). Flip to 20 if the event is shortened.
  consultLabel: "Book a free 30-minute consultation",
};

export const HERO = {
  overline: "Xero bookkeeping & fractional CFO - remote, Australia-wide",
  headline: "Your finance partner, without the full-time cost.",
  subheadline:
    "A CPA working alongside you to keep your books accurate, your numbers clear and your decisions confident.",
  primaryCta: "Book a free 30-minute consultation",
  secondaryCta: "Get the free Xero Health Check",
};

export const PAIN_POINTS = {
  overline: "Is this you?",
  title: "Sound familiar?",
  subtitle:
    "Most of our clients come to us with the same handful of problems. If any of these ring true, you're in the right place.",
  columns: [
    {
      icon: "Wrench",
      audience: "Trades businesses",
      points: [
        "Quoting and invoicing happen at night, and the books are months behind",
        "Receipts aren't organised, and getting ready for BAS takes days instead of minutes",
        "Wages, super and subbies to pay, but no clear picture of cash",
        "Your accountant asks questions you don't have time to answer",
      ],
    },
    {
      icon: "Rocket",
      audience: "Tech startups",
      points: [
        "Investor or grant reporting is due and the numbers don't tie out",
        "Xero was set up in a hurry on day one and never looked at again",
        "You can't state your real burn rate or runway with confidence",
        "The bookkeeper left and nobody really owns the numbers now",
      ],
    },
    {
      icon: "Briefcase",
      audience: "Service businesses",
      points: [
        "Invoices go out late, and chasing payment feels awkward",
        "You're flat out, but can't tell which clients actually make money",
        "GST, payroll and super keep you up at night",
        "Every BAS feels like a small emergency",
      ],
    },
  ],
};

export const TIERS = {
  overline: "Monthly support plans",
  title: "Pick how much of your finance function we carry",
  subtitle:
    "Fixed monthly fee, agreed before work starts. No hourly meters, no lock-in beyond the first three months. All plans produce accountant-ready books - your registered tax or BAS agent lodges.",
  items: [
    {
      name: "Essentials",
      promise: "Your books kept right and accountant-ready, every month.",
      price: "From $1,000/mo",
      bestFor: "Owners who just want tidy, lodgement-ready books",
      includes: [
        "Transaction coding and monthly bank reconciliations",
        "Receipt chasing via a simple capture app",
        "One-page monthly P&L summary in plain English",
        "Same-day support by email or phone, within your plan's scope",
      ],
    },
    {
      name: "Growth",
      promise: "The month properly closed, reported and explained.",
      price: "From $2,000/mo",
      bestFor: "Businesses with staff who want the full month-end done",
      includes: [
        "Everything in Essentials",
        "Month-end close + management report pack",
        "Payroll and super processing",
        "AP/AR processing - bills in, invoices out",
        "Monthly 30-minute check-in call",
      ],
      popular: true,
    },
    {
      name: "Control",
      promise: "Run the business on the numbers, not on gut feel.",
      price: "From $3,000/mo",
      bestFor: "Growing businesses making bigger decisions",
      includes: [
        "Everything in Growth",
        "13-week rolling cashflow forecast",
        "Budgets and job/project profitability",
        "App integrations and automation",
        "Fortnightly check-in calls",
      ],
    },
    {
      name: "Partner",
      promise: "A fractional CFO in your corner, a few days a month.",
      price: "From $4,000/mo",
      bestFor: "Startups and businesses wanting senior finance judgement",
      includes: [
        "Everything in Control",
        "KPI dashboard and board/investor reporting",
        "Pricing and scenario modelling",
        "Strategic advice as you grow",
        "Priority, first-in-queue response",
      ],
    },
  ],
};

export const COMPARISON = [
  { feature: "Coding & bank reconciliations", values: ["✓", "✓", "✓", "✓"] },
  { feature: "Monthly P&L summary", values: ["✓", "✓", "✓", "✓"] },
  { feature: "Month-end close + report pack", values: ["-", "✓", "✓", "✓"] },
  { feature: "Payroll & super", values: ["-", "✓", "✓", "✓"] },
  { feature: "AP/AR processing", values: ["-", "✓", "✓", "✓"] },
  { feature: "Cashflow forecast & budgets", values: ["-", "-", "✓", "✓"] },
  { feature: "Job / project profitability", values: ["-", "-", "✓", "✓"] },
  { feature: "Integrations & automation", values: ["-", "-", "✓", "✓"] },
  { feature: "KPI dashboard & board pack", values: ["-", "-", "-", "✓"] },
  { feature: "Scheduled check-ins", values: ["-", "Monthly call", "Fortnightly call", "2 meetings/mo"] },
  { feature: "Support (within plan scope)", values: ["Email & phone", "Email & phone", "Email & phone", "Email & phone"] },
  { feature: "Response time", values: ["Same day", "Same day", "Same day", "Same day (priority)"] },
];

export const ONE_OFFS = {
  overline: "One-off projects",
  title: "Fixed-scope projects, fixed prices",
  subtitle: "Every project starts with a written scope and a price you approve upfront.",
  items: [
    {
      name: "Xero Health Check",
      price: "Free - introductory offer",
      timeframe: "Up to 48 hours, depending on business size",
      badge: "Results within 48 hours",
      text: "A 15-point diagnostic of your Xero file with a written, prioritised fix list. The place to start.",
    },
    {
      name: "Xero Clean-Up Sprint",
      price: "From $1,000 - fixed quote after health check",
      timeframe: "Timeframe set by health check findings",
      text: "Historical reconciliations brought up to date, errors fixed, and a handover pack so your tax accountant can lodge.",
    },
    {
      name: "New Business Launch Pack",
      price: "$1,595 - includes ASIC fees",
      timeframe: "Up to 3 days",
      text: "Company and business name registration plus a Xero file set up correctly from day one. Start clean - never need a clean-up.",
    },
    {
      name: "Payroll compliance audit",
      price: "$950 fixed",
      timeframe: "1 week",
      text: "Pay items, super, leave accruals and STP settings checked, with a findings report for your accountant to act on. Up to 10 employees.",
    },
    {
      name: "System & app integrations",
      price: "From $1,200 per app",
      timeframe: "Confirmed once scope and app are locked in",
      text: "Connect the tools you run on - job management, POS, payments - to Xero, mapped and tested properly.",
    },
    {
      name: "EOFY accountant handover pack",
      price: "$750 - free on Growth and above",
      timeframe: "1 week",
      text: "A year-end reconciled file, workpapers index and adjustments list, packaged so your accountant can lodge fast.",
    },
  ],
};

export const PRICING_NOTES = [
  "Fixed quotes before any work starts",
  "3-month minimum, then month-to-month",
  "Prices exclude GST",
  "We prepare accountant-ready books - we don't lodge BAS or tax returns",
];

export const HOW_IT_WORKS = {
  overline: "How it works",
  title: "Three steps to a file you can trust",
  steps: [
    {
      title: "Book a free call",
      text: "30 minutes, no obligation. Tell us what's going on with your file and where you want the business to be.",
    },
    {
      title: "Health check & fixed quote",
      text: "We diagnose your Xero file, then give you a fixed scope and price before any work starts. No surprises.",
    },
    {
      title: "We fix it - and keep it clean",
      text: "We do the clean-up, hand your accountant a lodgement-ready file, then keep things tidy month to month so it never piles up again.",
    },
  ],
};

export const ABOUT = {
  overline: "About",
  title: "Hi, I'm Nima Riazati - CPA, and the person who'll actually do the work.",
  paragraphs: [
    "I'm a CPA with 15+ years in finance, including Finance Director and Financial Controller roles across technology, manufacturing, infrastructure and software businesses. I started Clarus Partners because small businesses deserve the same quality of finance support big companies get - without the full-time salary.",
    "When you work with Clarus, you work directly with me. No juniors, no handoffs, no call centres. I set up and clean up your Xero, run your operational finance, and explain everything in plain English.",
    "I'm remote by design, working with clients across Australia.",
  ],
  credentials: [
    "CPA (Certified Practising Accountant)",
    "15+ years in finance",
    "Former Finance Director & Financial Controller",
  ],
};

export const PROOF = {
  overline: "Proof",
  title: "A real result",
  story: {
    quote:
      "A founder was spending four hours every week reviewing reconciliations with their accountant. We fixed the underlying system issues, audited the historical transactions, and removed the need for the weekly call entirely.",
    outcome:
      "Their accountant now gets a clean file - and the founder got four hours a week back.",
    attribution: "Client story, details anonymised",
  },
};

export const WHY_CLARUS = {
  overline: "Why Clarus",
  title: "Senior help, without the senior hire",
  items: [
    {
      icon: "BadgeCheck",
      title: "CPA-qualified, senior only",
      text: "Your file is handled by a CPA with 15+ years' experience - never passed to a junior.",
    },
    {
      icon: "MapPin",
      title: "Remote, Australia-wide",
      text: "Wherever you are in Australia, we work in the cloud on Xero - same quality, no travel time billed.",
    },
    {
      icon: "ShieldCheck",
      title: "Your data stays safe",
      text: "We work inside Xero's bank-grade security, use least-access permissions and strong authentication, and never share your data.",
    },
    {
      icon: "FileCheck",
      title: "Built to hand over clean",
      text: "Everything we do is documented, so your tax accountant can lodge without the back-and-forth.",
    },
  ],
  badgesNote: "[NIMA TO PROVIDE: CPA member logo · NIMA TO CONFIRM: Xero partner/advisor badge]",
};

export const FAQ = {
  overline: "FAQ",
  title: "Fair questions, straight answers",
  items: [
    {
      q: "How much does it cost?",
      a: "Monthly support plans run from $1,000 to $4,000/month plus GST depending on how much of your finance function we carry - most clients sit on Growth at $2,000. One-off projects are fixed-priced: a Xero Clean-Up Sprint starts from $1,000, quoted after a free Health Check. You'll always know the price before work starts - no hourly meters running.",
    },
    {
      q: "How long does a clean-up take?",
      a: "The health check takes up to 48 hours, depending on the size of your business. Clean-up sprints are scoped off the health check findings - you'll get a firm timeframe with your fixed quote before any work starts.",
    },
    {
      q: "Do you work with MYOB, or just Xero?",
      a: "We specialise in Xero - it's what we're fastest and best at. If you're on MYOB or spreadsheets, we can move you across to Xero as part of a setup or clean-up.",
    },
    {
      q: "How does remote bookkeeping actually work?",
      a: "Everything runs in Xero and the cloud: receipts come in via a capture app or email, and questions are handled over email or a quick video call. You get the same output without booking meeting rooms - and it works the same whether you're in Sydney, regional Queensland or Perth.",
    },
    {
      q: "Is my financial data safe?",
      a: "Yes. Your data lives in Xero, which uses bank-grade encryption. We use least-privilege access, strong authentication on every account, and never share your data with anyone except your nominated accountant.",
    },
    {
      q: "Do you lodge my BAS or tax return?",
      a: "No - we're not a registered tax agent, and that's deliberate. We prepare a clean, accurate file so your tax accountant can lodge quickly (and usually charge you less). We work alongside your accountant, not instead of them.",
    },
    {
      q: "Can I just do a one-off clean-up, or do I have to sign up monthly?",
      a: "Either. Many clients start with a one-off Clean-Up Sprint and stay for monthly support once they see what tidy books feel like. There's no lock-in either way.",
    },
  ],
};

export const LEAD_MAGNET = {
  overline: "Free download",
  title: "The Xero Health Check Checklist",
  text: "15 checks you can run on your own Xero file in about 30 minutes - the same list we use before any clean-up. Find out if your file is lodgement-ready or quietly hiding problems.",
  bulletPoints: [
    "Bank recs, suspense accounts and GST codes",
    "The five red flags that delay your tax lodgement",
    "A simple scoring system: green, amber or red zone",
  ],
  formTitle: "Send me the checklist",
  button: "Email me the free checklist",
  privacyNote: "No spam, ever. Unsubscribe any time.",
};

export const QUOTE_FORM = {
  overline: "Get a quote",
  title: "Tell us what's going on with your file",
  subtitle:
    "A few quick details and we'll come back within one business day with a recommended next step and indicative pricing.",
};

export const FINAL_CTA = {
  title: "Ready to get your Xero out of the red zone?",
  text: "Book a free 30-minute consultation. Bring your questions - leave with a clear next step, whether we work together or not.",
};

export const FOOTER = {
  tagline: "Remote finance support for Australian small business.",
  abn: "ABN 49 308 724 557",
};

export const HEALTH_CHECK_PAGE = {
  overline: "Free checklist",
  headline: "Is your Xero file lodgement-ready - or quietly falling behind?",
  subheadline:
    "Get the free Xero Health Check Checklist: 15 checks you can run in about 30 minutes, using the same diagnostic list a CPA runs before any clean-up.",
  insideTitle: "What's inside",
  inside: [
    "The exact 15-point checklist we use before every clean-up engagement",
    "The five red flags that delay tax lodgement (and how to spot them)",
    "A simple scoring system so you know if you're green, amber or red",
  ],
  whoTitle: "Who it's for",
  who: [
    "Trades businesses whose records aren't organised until BAS time",
    "Startup founders who need investor-ready numbers",
    "Service business owners who want their evenings back",
  ],
  preferCall: "Prefer to talk it through?",
};
